import { Feature, hasFeature } from '@/featureFlags/featureFlags'
import { TeamState } from '@/store/types'
import { KeyDict } from '@/types'
import { DEFAULT_SUPPORT_LEVEL, SupportLevel } from '@/types/casts'
import { UserProfile } from '@/types/users'
import { CastProperty, CastPropertyType } from '@/types/admin'
import { DropdownOption } from '@/types/dropdownOption'

// getters
const getters = {
  user: (state: TeamState) => (userId: string) => {
    return (state.currentTeam || []).find( (user:UserProfile) => user.user_id === userId)
  },
  joinNotification: (state: TeamState) => (userId: string, link: string) => {
    const member = (state.currentTeam || []).find(user => user.user_id === userId)
    if (!member) {
      return false
    }
    return member.notifications
      && Object.values(member.notifications).find(notification => notification.link === link)
  },
  defaultSupportLevel: (_state: TeamState): SupportLevel => {
    return hasFeature(Feature.SELF_SERVE) ? SupportLevel.Lite : DEFAULT_SUPPORT_LEVEL
  },
  allowedStreamQualities: (state: TeamState): KeyDict<CastProperty> => {
    const allCastProperties = state.castProperties
    const allowedQualities: KeyDict<CastProperty> = {}
    const isSelfServe = hasFeature(Feature.SELF_SERVE)
    for (const [castPropertyId, castProperty] of Object.entries(allCastProperties)) {
      if (castProperty.type !== CastPropertyType.Quality) continue
      // If Self-Serve, only include qualities <= 1080p 10mbps 30fps
      // Below parses out mbps as an int without leading 0's for comparison, ex., 00720p00600 -> 600
      if (isSelfServe && +castPropertyId.split('p')[1] > 10000) continue
      allowedQualities[castPropertyId] = castProperty
    }
    return allowedQualities
  },
  allowedStreamFramerates: (state: TeamState): KeyDict<CastProperty> => {
    const allCastProperties = state.castProperties
    const allowedFramerates: KeyDict<CastProperty> = {}
    const isSelfServe = hasFeature(Feature.SELF_SERVE)
    for (const [castPropertyId, castProperty] of Object.entries(allCastProperties)) {
      if (castProperty.type !== CastPropertyType.Framerate) continue
      // If Self-Serve, only include fps <= 30
      if (isSelfServe && parseFloat(castPropertyId) > 30) continue
      allowedFramerates[castPropertyId] = castProperty
    }
    return allowedFramerates
  },
  allowedStreamQualityDropdownOptions: (_state: TeamState, getters: KeyDict<Function>): DropdownOption[] => {
    const allowedQualities = getters.allowedStreamQualities
    const sortedKeys = Object.keys(allowedQualities).sort()
    return sortedKeys.map((key) => ({ name: allowedQualities[key].name, key: key }))
  },
  allowedStreamFramerateDropdownOptions: (_state: TeamState, getters: KeyDict<Function>): DropdownOption[] => {
    const allowedFramerates = getters.allowedStreamFramerates
    const sortedKeys = Object.keys(allowedFramerates).sort()
    return sortedKeys.map(key => ({ name: allowedFramerates[key].name, key: key.replace('Hz', '') }))
  }
}

export default getters
